import { PORTAL_NAME } from '@admin-panel/config/env';
import { shouldDropNoisyClientError, shouldUseSentry } from './sentry-config';
import { serializeForSentry, toHeaderRecord } from './sentry-serialization';
import type { HttpBreadcrumbPayload, PortalUserLike } from './sentry-types';

declare const __SENTRY_RELEASE__: string | null;
declare const __SENTRY_ENVIRONMENT__: string | null;

type SentryModule = typeof import('@sentry/react');

let sentryModulePromise: Promise<SentryModule> | null = null;

const loadSentry = () => {
  sentryModulePromise ??= import('@sentry/react');
  return sentryModulePromise;
};

const withSentry = (callback: (sentry: SentryModule) => void) => {
  if (!shouldUseSentry) {
    return;
  }

  void loadSentry()
    .then(callback)
    .catch(error => {
      console.warn('Could not load Sentry client', error);
    });
};

const getRoleNames = (roles?: PortalUserLike['roles']) =>
  roles?.map(role => (typeof role === 'string' ? role : role?.name)).filter(Boolean) ?? [];

export const initPortalSentry = (tracePropagationTargets: Array<string>) => {
  withSentry(Sentry => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: __SENTRY_ENVIRONMENT__ ?? undefined,
      release: __SENTRY_RELEASE__ ?? undefined,
      sendDefaultPii: true,
      tracePropagationTargets,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: 1,
      profilesSampleRate: 1,
      replaysSessionSampleRate: 1,
      replaysOnErrorSampleRate: 1,
      beforeSend(event, hint) {
        if (shouldDropNoisyClientError(event, hint)) {
          return null;
        }

        return event;
      },
    });

    Sentry.setTag('portal', PORTAL_NAME);
  });
};

export const setPortalSentryUser = (user: PortalUserLike | null | undefined) => {
  withSentry(Sentry => {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setContext('erpUser', null);
      return;
    }

    Sentry.setTag('portal', PORTAL_NAME);
    Sentry.setUser({
      id: user.id != null ? String(user.id) : undefined,
      email: user.email ?? undefined,
      username: user.name ?? undefined,
    });
    Sentry.setContext('erpUser', {
      id: user.id != null ? String(user.id) : undefined,
      email: user.email ?? undefined,
      name: user.name ?? undefined,
      userType: user.userType ?? undefined,
      roles: getRoleNames(user.roles),
    });
  });
};

export const clearPortalSentryUser = () => {
  withSentry(Sentry => {
    Sentry.setUser(null);
    Sentry.setContext('erpUser', null);
  });
};

export const setPortalSentryTag = (
  key: string,
  value: string | number | boolean | null | undefined,
) => {
  if (value == null) {
    return;
  }

  withSentry(Sentry => {
    Sentry.setTag(key, value);
  });
};

export const hydratePortalSentryUserFromStorage = () => {
  if (!shouldUseSentry || typeof window === 'undefined') {
    return;
  }

  const rawUser = localStorage.getItem('user');
  if (!rawUser) {
    clearPortalSentryUser();
    return;
  }

  try {
    setPortalSentryUser(JSON.parse(rawUser) as PortalUserLike);
  } catch (error) {
    console.warn('Could not parse stored user for Sentry context', error);
    clearPortalSentryUser();
  }
};

export const addHttpBreadcrumb = ({
  transport,
  phase,
  request,
  response,
  error,
}: HttpBreadcrumbPayload) => {
  withSentry(Sentry => {
    Sentry.addBreadcrumb({
      type: 'http',
      category: `http.${transport}.${phase}`,
      level: phase === 'error' ? 'error' : 'info',
      data: {
        request: {
          url: request.url,
          method: request.method,
          headers: toHeaderRecord(request.headers),
          body: serializeForSentry(request.body),
          params: serializeForSentry(request.params),
        },
        response: response
          ? {
              status: response.status,
              durationMs: response.durationMs,
              headers: toHeaderRecord(response.headers),
              body: serializeForSentry(response.body),
            }
          : undefined,
        error: serializeForSentry(error),
      },
    });
  });
};

export const setSynqSentryUser = (user: PortalUserLike | null | undefined) =>
  setPortalSentryUser(user);

export const clearSynqSentryUser = () => clearPortalSentryUser();

export const hydrateSynqSentryUserFromStorage = () => hydratePortalSentryUserFromStorage();

export const addSynqHttpBreadcrumb = (payload: HttpBreadcrumbPayload) => addHttpBreadcrumb(payload);

export { buildSentryTracePropagationTargets } from './sentry-config';
